import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grow,
  useTheme,
  alpha,
  Link
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DoneIcon from '@mui/icons-material/Done';
import CheckIcon from '@mui/icons-material/Check';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useTour } from '../../context/TourContext';
import { useUI } from '../../context/UIContext';

const SPOTLIGHT_PADDING = 8;
const MARGIN = 16;

const OnboardingTour = () => {
  const {
    isOpen, currentStep, nextStep, prevStep, closeTour,
    isLastStep, isFirstStep, currentStepIndex, steps, isSingleStep
  } = useTour();
  const uiContext = useUI();
  const { isMobile } = uiContext;
  const theme = useTheme();
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef(null);
  const updateTimeoutRef = useRef(null);

  // Helper to determine if we are in a modal/welcome step (no specific target)
  const isModalStep = currentStep && !currentStep.targetId;

  useEffect(() => {
    if (!isOpen || !currentStep) return;

    const executeStepAction = async () => {
      if (currentStep.onBeforeShow) {
        try {
          await currentStep.onBeforeShow({
            ui: uiContext,
            doc: document
          });
        } catch (e) {
          console.error("[OnboardingTour] Step action failed:", e);
        }
      }
      
      // If it's a modal step, update immediately. If target, wait for UI transitions.
      const delay = isModalStep ? 0 : 450;
      updateTimeoutRef.current = setTimeout(updatePosition, delay);
    };

    executeStepAction();

    return () => {
        if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);
    };
  }, [isOpen, currentStep, uiContext, isModalStep]);

  const updatePosition = () => {
    if (!isOpen || !currentStep) return;

    // If it's a modal step (no target), we clear the target rect.
    // Positioning is handled via Flexbox in the render method now.
    if (!currentStep.targetId) {
      setTargetRect(null);
      return;
    }

    const element = document.getElementById(currentStep.targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      const rect = element.getBoundingClientRect();
      
      setTargetRect({
        top: rect.top - SPOTLIGHT_PADDING,
        left: rect.left - SPOTLIGHT_PADDING,
        width: rect.width + (SPOTLIGHT_PADDING * 2),
        height: rect.height + (SPOTLIGHT_PADDING * 2),
      });

      const tooltipWidth = tooltipRef.current ? tooltipRef.current.offsetWidth : 320;
      const tooltipHeight = tooltipRef.current ? tooltipRef.current.offsetHeight : 200;
      
      let placement = currentStep.placement || 'bottom';

      // Mobile adjustment logic
      if (isMobile) {
        const spaceAbove = rect.top;
        const spaceBelow = window.innerHeight - rect.bottom;
        const heightNeeded = tooltipHeight + MARGIN + SPOTLIGHT_PADDING;
        
        const isHugeTarget = rect.height > (window.innerHeight * 0.8);
        
        if (!isHugeTarget) {
            if (spaceBelow >= heightNeeded) {
                placement = 'bottom';
            } else if (spaceAbove >= heightNeeded) {
                placement = 'top';
            } else {
                placement = spaceBelow > spaceAbove ? 'bottom' : 'top';
            }
        } else {
            placement = 'bottom';
        }
      }

      const calculateCoords = (place) => {
        switch (place) {
          case 'right':
            return {
              top: rect.top + (rect.height / 2) - (tooltipHeight / 2),
              left: rect.right + MARGIN + SPOTLIGHT_PADDING
            };
          case 'left':
            return {
              top: rect.top + (rect.height / 2) - (tooltipHeight / 2),
              left: rect.left - tooltipWidth - MARGIN - SPOTLIGHT_PADDING
            };
          case 'top':
            return {
              top: rect.top - tooltipHeight - MARGIN - SPOTLIGHT_PADDING,
              left: rect.left + (rect.width / 2) - (tooltipWidth / 2)
            };
          case 'bottom':
          default:
            return {
              top: rect.bottom + MARGIN + SPOTLIGHT_PADDING,
              left: rect.left + (rect.width / 2) - (tooltipWidth / 2)
            };
        }
      };

      let coords = calculateCoords(placement);

      // Boundary checks for desktop
      if (!isMobile) {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        if (placement === 'right' && (coords.left + tooltipWidth > viewportWidth)) {
            coords = calculateCoords('left');
        } else if (placement === 'left' && (coords.left < 0)) {
            coords = calculateCoords('right');
        } else if (placement === 'top' && (coords.top < 0)) {
            coords = calculateCoords('bottom');
        } else if (placement === 'bottom' && (coords.top + tooltipHeight > viewportHeight)) {
            coords = calculateCoords('top');
        }
      }

      // Screen edge clamping
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (coords.left < MARGIN) coords.left = MARGIN;
      if (coords.left + tooltipWidth > viewportWidth - MARGIN) {
        coords.left = viewportWidth - tooltipWidth - MARGIN;
      }
      if (coords.top < MARGIN) coords.top = MARGIN;
      if (coords.top + tooltipHeight > viewportHeight - MARGIN) {
        coords.top = viewportHeight - tooltipHeight - MARGIN;
      }
      
      // Center horizontally on mobile for top/bottom placements
      if (isMobile) {
          coords.left = (viewportWidth - tooltipWidth) / 2;
      }

      setTooltipPosition(coords);
    } else {
      console.warn(`Tour target #${currentStep.targetId} not found.`);
      setTargetRect(null);
    }
  };

  useLayoutEffect(() => {
    if (isOpen) {
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
    }
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (isOpen) updatePosition();
  }, [currentStepIndex, isOpen]);

  if (!isOpen) return null;
  
  // If we are looking for a target but haven't found it yet, don't render (prevents flash)
  // Unless it's a modal step which doesn't need a target.
  if (!isModalStep && !targetRect) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1500,
        pointerEvents: 'auto',
        // Flexbox centering for modal steps ensures perfect centering on mobile
        // without relying on transforms that conflict with Grow animations
        display: isModalStep ? 'flex' : 'block',
        alignItems: isModalStep ? 'center' : undefined,
        justifyContent: isModalStep ? 'center' : undefined,
      }}
    >
      {/* Spotlight / Backdrop */}
      {targetRect && targetRect.width > 0 ? (
        <Box
          sx={{
            position: 'absolute',
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            borderRadius: 1,
            border: `3px solid ${theme.palette.primary.main}`,
            boxShadow: `
              0 0 15px 2px ${alpha(theme.palette.primary.main, 0.6)},
              0 0 0 9999px ${alpha(theme.palette.common.black, 0.7)}
            `,
            pointerEvents: 'none',
            transition: 'all 0.3s ease-in-out',
            zIndex: 1501,
          }}
        />
      ) : (
         // Full Backdrop for modal steps or missing targets
         <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundColor: alpha(theme.palette.common.black, 0.7),
              pointerEvents: 'none',
              zIndex: 1501,
            }}
         />
      )}

      {/* Tooltip Card */}
      <Grow in={true} key={currentStepIndex}>
        <Paper
          ref={tooltipRef}
          elevation={8}
          sx={{
            // If modal, let Flexbox center it (relative). If target, use absolute positioning.
            position: isModalStep ? 'relative' : 'absolute',
            top: isModalStep ? 'auto' : tooltipPosition.top,
            left: isModalStep ? 'auto' : tooltipPosition.left,
            
            width: 320,
            maxWidth: '90vw',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            p: 0,
            overflow: 'hidden',
            borderRadius: 2,
            transition: isModalStep ? 'none' : 'top 0.3s ease-out, left 0.3s ease-out',
            zIndex: 1502,
            outline: 'none', // Remove focus outline if clicked
          }}
        >
          <Box sx={{
            p: 1.5,
            px: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            flexShrink: 0,
          }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {currentStep.title}
            </Typography>
          </Box>
          
          <Box sx={{ p: 2, overflowY: 'auto', flexGrow: 1 }}>
            <Typography variant="body2" color="text.primary" sx={{ mb: 3, lineHeight: 1.5 }}>
              {currentStep.content}
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {!isSingleStep && (
                  <Typography variant="caption" color="text.secondary">
                    Step {currentStepIndex + 1} of {steps.length}
                  </Typography>
                )}
                {!isSingleStep && !isLastStep && (
                  <Link
                    component="button"
                    variant="caption"
                    onClick={closeTour}
                    sx={{
                      color: 'text.disabled',
                      textDecoration: 'none',
                      textAlign: 'left',
                      '&:hover': { textDecoration: 'underline', color: 'text.secondary' }
                    }}
                  >
                    (Skip Tour)
                  </Link>
                )}
              </Box>
              
              <Box>
                {!isSingleStep && !isModalStep && (
                  <Button
                    size="small"
                    onClick={prevStep}
                    disabled={isFirstStep}
                    sx={{ mr: 1, minWidth: 'auto' }}
                  >
                    <ArrowBackIcon fontSize="small" />
                  </Button>
                )}
                
                <Button
                  variant="contained"
                  size="small"
                  onClick={nextStep}
                  endIcon={
                    isLastStep 
                      ? (isSingleStep ? <CheckIcon /> : <DoneIcon />) 
                      : (isModalStep ? <PlayArrowIcon /> : <ArrowForwardIcon />)
                  }
                >
                  {isLastStep 
                    ? (isSingleStep ? 'Got it' : 'Finish') 
                    : (isModalStep ? 'Start' : 'Next')
                  }
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Grow>
    </Box>
  );
};

export default OnboardingTour;